/* eslint-disable @typescript-eslint/naming-convention */

import React, {FC, useCallback, useState} from 'react';
import {Swiper as ThumbSlider, SwiperClass, SwiperSlide} from 'swiper/react';
import {FreeMode, Thumbs} from 'swiper/modules';
import Picture from 'components/Picture';
import {Slider} from '../Slider';
import {MagnifierImagePopup} from '../MagnifierImagePopup';
import 'react-inner-image-zoom/lib/styles.min.css';
import style from './style.module.scss';
import cs from 'classnames';
import {Magnifier, MagnifierCtxConsumer} from 'components/Magnifier';
import {CSSProperties} from 'react';
import {getMediaSrc} from 'core/helpers/media';
import {makeGallerySources} from 'modules/product-card/helpers';
import Icon from 'components/Icon';
import {IBubble, IMedia} from 'modules/products/types';
import {useRouter} from 'next/router';
import {PRODUCT_BASE_ROUTE} from 'core/modules/routing/constants';
import {showModal} from 'modules/modals/thunks';
import {MODALS} from 'modules/modals/constants';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {useProductMediaState} from 'modules/product-card/mediaState/useProductMediaState';
import {SkeletonWrapper} from 'modules/skeleton/components/SkeletonWrapper';

const THUMB_BREAKPOINTS = {
    0: {
        spaceBetween: 8,
    },
    768: {
        spaceBetween: 12,
    },
};

interface IProps {
    images: string[];
    bubble?: IBubble;
    popupDirection?: 'left' | 'right';
    thumbSliderDirection?: 'horizontal' | 'vertical';
    thumbSlidesPerView?: 'auto' | number;
    hideThumbIfOneImage?: boolean;
    imagesAltText?: string;
    onlyThumbsSlider?: boolean;
    isReviewsPage?: boolean;
    videoUrl?: string;
    videoImage?: IMedia['videoPreview'];
}

interface IThumbSliderSectionProps {
    images: string[];
    onlyThumbsSlider: boolean;
    thumbSliderDirection: 'horizontal' | 'vertical';
    thumbSlidesPerView: 'auto' | number;
    onThumbsSwiper: (swiper: SwiperClass) => void;
    handleImageClick: (img: string) => () => void;
    videoUrl?: string;
    videoImage?: IMedia['videoPreview'];
    handleVideoClick: () => void;
}

const ThumbSliderSection: FC<IThumbSliderSectionProps> = ({
    images,
    onlyThumbsSlider,
    thumbSliderDirection,
    thumbSlidesPerView,
    onThumbsSwiper,
    handleImageClick,
    videoUrl,
    videoImage,
    handleVideoClick,
}) => {
    const [isThumbsInitialized, setIsThumbsInitialized] = useState(false);
    const skeletonCount = Math.min(Math.max(images.length + (videoUrl ? 1 : 0), 1), 6);

    return (
        <>
            {!isThumbsInitialized && (
                <SkeletonWrapper disabled={isThumbsInitialized}>
                    <div className={style.thumbSkeletons}>
                        {Array.from({length: skeletonCount}).map((_, idx) => (
                            <div
                                className={cs(style.thumbSlide, style.thumbSkeletonItem)}
                                data-skeleton-item={true}
                                key={`thumb-skeleton-${idx}`}
                            />
                        ))}
                    </div>
                </SkeletonWrapper>
            )}
            <ThumbSlider
                breakpoints={THUMB_BREAKPOINTS}
                className={cs(style.thumbSlider, 'media-magnifier-thumb-slider')}
                style={{visibility: isThumbsInitialized ? 'visible' : 'hidden'}}
                direction={thumbSliderDirection}
                freeMode={true}
                loop={true}
                modules={[Thumbs, FreeMode]}
                onSwiper={(swiper) => {
                    setIsThumbsInitialized(true);
                    onThumbsSwiper(swiper);
                }}
                slidesPerView={thumbSlidesPerView}
                watchSlidesProgress={true}
                preventClicksPropagation={false}
                preventClicks={false}
            >
                {images.map((image) => {
                    const imageSrc = getMediaSrc(image);
                    const onClick = onlyThumbsSlider ? handleImageClick(imageSrc) : undefined;

                    return (
                        <SwiperSlide key={image} className={cs(style.thumbSlide, 'media-magnifier-thumb-slide')}>
                            <div className={style.slideWrapper} onClick={onClick}>
                                <Picture
                                    alt={''}
                                    className={style.thumbPicture}
                                    sources={{}}
                                    src={getMediaSrc(image, {
                                        height: 150,
                                    })}
                                />
                            </div>
                        </SwiperSlide>
                    );
                })}
                {videoUrl && (
                    <div className={style.slideWrapper} onClick={handleVideoClick}>
                        <div className={cs(style.thumbSlide, 'media-magnifier-thumb-slide')}>
                            <Picture alt={''} className={style.thumbPicture} sources={{}} src={videoImage} />
                            <Icon icon={'A_Icon_Play'} width={40} height={40} />
                        </div>
                    </div>
                )}
            </ThumbSlider>
            {/* фоллбек на миниатюры если нет js */}
            <noscript>
                <style>{`.media-magnifier-thumb-slider{display:none!important;}`}</style>
                <div>
                    {images.map((image) => {
                        return (
                            <div className={style.thumbSlide} key={image}>
                                <div className={style.slideWrapper}>
                                    <img
                                        alt={''}
                                        className={style.thumbPicture}
                                        src={getMediaSrc(image, {height: 84})}
                                    />
                                </div>
                            </div>
                        );
                    })}
                    {videoUrl && (
                        <div className={style.slideWrapper} onClick={handleVideoClick}>
                            <div className={style.slideWrapper}>
                                <img alt={''} className={style.thumbPicture} src={videoImage} />
                            </div>
                        </div>
                    )}
                </div>
            </noscript>
        </>
    );
};

interface IMainSliderSectionProps {
    images: string[];
    popupDirection: 'left' | 'right';
    imagesAltText: string;
    thumbsSwiper: SwiperClass | null;
    handleImageClick: (img: string) => () => void;
}

const MainSliderSection: FC<IMainSliderSectionProps> = ({
    images,
    popupDirection,
    imagesAltText,
    thumbsSwiper,
    handleImageClick,
}) => {
    const magnifierCursorCtx = useCallback((styleCtx?: CSSProperties) => {
        return <div className={cs(style.magnifierCursor, 'magnifier-cursor')} style={styleCtx} />;
    }, []);

    return (
        <Magnifier popupDirection={popupDirection}>
            <MagnifierCtxConsumer>
                {({cursorStyles, onMouseEnter}) => (
                    <Slider
                        centeredSlides={true}
                        className={cs(style.slider, 'media-magnifier-slider')}
                        loop={false}
                        magnifierCursor={magnifierCursorCtx(cursorStyles)}
                        modules={[Thumbs, FreeMode]}
                        slidesPerGroup={1}
                        slidesPerView={1}
                        thumbs={{
                            swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
                        }}
                        initialSlide={0}
                        resistanceRatio={0}
                        simulateTouch={false}
                    >
                        {images.map((image) => {
                            const imageSrc = getMediaSrc(image);

                            return (
                                <SwiperSlide
                                    className={style.slide}
                                    key={imageSrc}
                                    onMouseEnter={onMouseEnter?.(getMediaSrc(image, {height: 1208, width: 640}))}
                                >
                                    <div className={style.slideWrapper} onClick={handleImageClick(imageSrc)}>
                                        <Picture
                                            alt={imagesAltText}
                                            className={style.pictureMobile}
                                            sources={makeGallerySources(image)}
                                            src={imageSrc}
                                        />
                                    </div>
                                </SwiperSlide>
                            );
                        })}
                    </Slider>
                )}
            </MagnifierCtxConsumer>
        </Magnifier>
    );
};

interface IPopupHintProps {
    bubble?: IBubble;
    shouldShowPopUpHint: boolean;
    onClose: () => void;
}

const PopupHint: FC<IPopupHintProps> = ({bubble, shouldShowPopUpHint, onClose}) => {
    if (!bubble?.isActive || !bubble?.text || !shouldShowPopUpHint) {
        return null;
    }

    return (
        <div className={cs(style.popUp, 'popup-hint')}>
            {/* eslint-disable @typescript-eslint/naming-convention */}
            <div dangerouslySetInnerHTML={{__html: bubble.text}} />
            <div className={style.popUpCloseIconWrapper} onClick={onClose}>
                <Icon icon={'close'} />
            </div>
        </div>
    );
};

// eslint-disable-next-line max-lines-per-function
export const MediaMagnifierSlider: FC<IProps> = ({
    bubble,
    images = [],
    imagesAltText = '',
    popupDirection = 'left',
    thumbSliderDirection = 'vertical',
    thumbSlidesPerView = 'auto',
    hideThumbIfOneImage = false,
    onlyThumbsSlider = false,
    isReviewsPage = false,
    videoUrl,
    videoImage,
}) => {
    const dispatch = useAppDispatch();
    const router = useRouter();

    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);
    const [openedImage, setOpenedImage] = useState<string | null>(null);
    const [shouldShowPopUpHint, setShouldShowPopUpHint] = useState<boolean>(true);

    const productCode = router.query.code as string;

    const handleImageClick = useCallback(
        (img: string) => () => {
            if (768 <= window.innerWidth) {
                // https://kb.simple.ru/pages/viewpage.action?pageId=131634550#UC61Просмотротзывовотоваре(детальнаястраница)-COMP-4Компонент"Товар"
                if (isReviewsPage && productCode) {
                    router.push(`${PRODUCT_BASE_ROUTE}${productCode}`);
                }
                return;
            }

            setOpenedImage(img);
        },
        [isReviewsPage, productCode, router],
    );

    const [_, setProductMediaState] = useProductMediaState();

    const handleVideoClick = useCallback(() => {
        if (videoUrl) {
            setProductMediaState((state) => ({...state, mediaSrc: videoUrl, mediaType: 'video'}));
            dispatch(showModal(MODALS.PRODUCT_VIDEO_MODAL.name));
        }
    }, [dispatch, videoUrl, setProductMediaState]);

    const handleImagePopupClose = () => setOpenedImage(null);
    const handlePopUpHintClose = () => setShouldShowPopUpHint(false);

    const showThumbSlider = !hideThumbIfOneImage || 1 !== images.length;

    return (
        <>
            <div className={cs(style.sliderWrapper, 'media-magnifier-wrapper')}>
                {showThumbSlider && (
                    <div className={cs(style.thumbSlider, 'thumb-slider')}>
                        <ThumbSliderSection
                            images={images}
                            onlyThumbsSlider={onlyThumbsSlider}
                            thumbSliderDirection={thumbSliderDirection}
                            thumbSlidesPerView={thumbSlidesPerView}
                            onThumbsSwiper={setThumbsSwiper}
                            handleImageClick={handleImageClick}
                            videoUrl={videoUrl}
                            videoImage={videoImage}
                            handleVideoClick={handleVideoClick}
                        />
                    </div>
                )}
                {!onlyThumbsSlider && (
                    <MainSliderSection
                        images={images}
                        popupDirection={popupDirection}
                        imagesAltText={imagesAltText}
                        thumbsSwiper={thumbsSwiper}
                        handleImageClick={handleImageClick}
                    />
                )}
                <PopupHint bubble={bubble} shouldShowPopUpHint={shouldShowPopUpHint} onClose={handlePopUpHintClose} />
            </div>
            <MagnifierImagePopup
                image={openedImage}
                isOpen={Boolean(openedImage)}
                onClose={handleImagePopupClose}
                isReviewsPage={isReviewsPage}
            />
        </>
    );
};
